class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3 seconds
    this.listeners = new Map();
    this.isConnected = false;
    this.url = process.env.REACT_APP_WS_URL;
    this.subscriptions = new Set(); // Track active subscriptions
    this.isReconnecting = false;
  }

  // Connect to WebSocket
  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = (event) => {
        console.log('WebSocket connected to lighter stream');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.isReconnecting = false;

        // Restore all subscriptions after reconnection
        this.restoreSubscriptions();

        this.emit('connected', event);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle ping/pong heartbeat
          if (data.type === 'ping') {
            this.send({ type: 'pong' });
            return; // Don't emit ping messages to listeners
          }

          this.emit('message', data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          this.emit('rawMessage', event.data);
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        this.isConnected = false;

        // Track if this was an unexpected disconnection (not manual disconnect)
        if (event.code !== 1000) {
          this.isReconnecting = true;
        }

        this.emit('disconnected', event);

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', error);
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.emit('error', error);
    }
  }

  // Disconnect from WebSocket
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.isConnected = false;
    this.isReconnecting = false;
    // Note: We don't clear subscriptions here so they can be restored on reconnect
  }

  // Send message to WebSocket
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.ws.send(message);
    } else {
      console.warn('WebSocket is not connected. Cannot send message:', data);
    }
  }

  // Subscribe to events
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Unsubscribe from events
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Emit events to listeners
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket event listener:', error);
        }
      });
    }
  }

  // Attempt to reconnect with exponential backoff
  attemptReconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);

    setTimeout(() => {
      if (!this.isConnected) {
        this.connect();
      }
    }, delay);
  }

  // Get connection status
  getStatus() {
    return {
      isConnected: this.isConnected,
      readyState: this.ws ? this.ws.readyState : WebSocket.CLOSED,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  // Subscribe to specific lighter data types
  subscribeToTrades(marketId) {
    this.send({
      type: 'subscribe',
      channel: `trade/${marketId}`
    });
  }

  subscribeToOrderbook(marketId) {
    this.send({
      type: 'subscribe',
      channel: `order_book/${marketId}`,
    });
  }

  subscribeToAccount(accountIndex) {
    const channel = `account_all/${accountIndex}`;
    this.subscriptions.add(channel);

    this.send({
      type: 'subscribe',
      channel: channel
    });
  }

  // Unsubscribe from channels
  unsubscribe(channel, symbol = null) {
    this.subscriptions.delete(channel);

    const unsubscribeData = {
      type: 'unsubscribe',
      channel: channel
    };
    if (symbol) {
      unsubscribeData.symbol = symbol;
    }
    this.send(unsubscribeData);
  }

  // Restore all subscriptions after reconnection
  restoreSubscriptions() {
    if (this.subscriptions.size > 0) {
      this.subscriptions.forEach(channel => {
        this.send({
          type: 'subscribe',
          channel: channel
        });
      });
    }
  }

  // Get current subscriptions
  getSubscriptions() {
    return Array.from(this.subscriptions);
  }

  // Clear all subscriptions (useful for cleanup)
  clearSubscriptions() {
    this.subscriptions.clear();
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
